/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import {nullify, ResponseData, showDialog} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {QR_CODES_ROUTE} from '../../util/constants';
import {downloadFileResponse} from '../../util/downloadUtil';
import {onSetCrumbs} from '../../util/events';
import {QrCodeProduct} from '../../util/interfaces';
import {generateAllQrCodes, generateQrCode, getProductsV2} from '../../util/requestSender';
import {createErrorMessage} from '../../util/utils';
import {AppContext} from '../misc/appContext';
import QrCodes from './qrCodes';

const DEBOUNCE = 500;
const DEFAULT_LIMIT = 50;
const LOAD_MORE_PAD_ROWS = 5;

export default function QrCodesController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const account = context.accountContext.account;
    const [products, setProducts] = useState<QrCodeProduct[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
    const [downloadingAll, setDownloadingAll] = useState(false);
    const searchDebounce = useRef<number | null>(null);
    const productsAbortController = useRef<AbortController | null>(null);
    const loadingMoreRef = useRef(false);
    const appliedSearchRef = useRef<string | null>(null);
    const cursorRef = useRef<string | null>(null);

    function onChangeSearch(value: string) {
        clearDebounce();
        setSearch(value);
        searchDebounce.current = window.setTimeout(() => {
            cancelRequests();
            loadProducts(true, {search: value, force: true})();
        }, DEBOUNCE);
    }

    function onSearch() {
        clearDebounce();
        cancelRequests();
        loadProducts(true, {force: true})();
    }

    function onRowVisible(ix: number) {
        return function () {
            attemptLoadMore(ix);
        };
    }

    function onClickDownload(product: QrCodeProduct) {
        return function () {
            if (downloadingIds.has(product.id)) return;
            setDownloadingIds(current => new Set(current).add(product.id));
            generateQrCode({
                account_id: product.account_id,
                product_id: product.id,
                callback: (data: ResponseData, response: Response) => {
                    removeDownloadingId(product.id);
                    if (data.status === 'success') downloadFileResponse(response, `review-qr-${product.ean}.png`);
                    else if (!data.handled) showDownloadError(data.message);
                },
                errorCallback: () => {
                    removeDownloadingId(product.id);
                    showDownloadError();
                }
            });
        };
    }

    function onClickDownloadAll() {
        if (downloadingAll || !account) return;
        setDownloadingAll(true);
        generateAllQrCodes({
            account_id: account.id,
            callback: (data: ResponseData, response: Response) => {
                setDownloadingAll(false);
                if (data.status === 'success') downloadFileResponse(response, 'review-qr.zip');
                else if (!data.handled) showDownloadError(data.message);
            },
            errorCallback: () => {
                setDownloadingAll(false);
                showDownloadError();
            }
        });
    }

    function removeDownloadingId(id: string) {
        setDownloadingIds(current => {
            const next = new Set(current);
            next.delete(id);
            return next;
        });
    }

    function showDownloadError(message?: string | null) {
        showDialog(
            createErrorMessage(
                message ?? t('qrCodes.download.failed', 'Het downloaden van de QR-code is niet gelukt.')
            ),
            t('qrCodes.download.failedTitle', 'Downloaden mislukt')
        );
    }

    function showLoadError(message?: string | null) {
        showDialog(
            createErrorMessage(message ?? t('qrCodes.load.failed', 'Het laden van de producten is niet gelukt.')),
            t('qrCodes.load.failedTitle', 'Laden mislukt')
        );
    }

    function attemptLoadMore(ix: number) {
        if (!hasMore || loadingProducts || loadingMoreRef.current) return;
        if (ix < products.length - LOAD_MORE_PAD_ROWS) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        loadProducts(false, {cursor: cursorRef.current, append: true, force: true})();
    }

    function loadProducts(
        load: boolean = true,
        params?: {search?: string; cursor?: string | null; append?: boolean; continued?: boolean; force?: boolean}
    ) {
        return function () {
            if (!params?.append && !params?.continued) clearDebounce();
            if (loadingProducts && !params?.force) return;
            if (!account) return;
            const _search = params?.append ? appliedSearchRef.current : nullify(params?.search ?? search);
            const _cursor = params?.cursor ?? null;
            if (load) setLoadingProducts(true);
            const controller = getProductsV2({
                data: {
                    account_ids: [account.id],
                    search: _search,
                    limit: DEFAULT_LIMIT,
                    last_product_ean: _cursor,
                    require_meta: true,
                    load_meta: true,
                    expand: ['qr_scan_counts']
                },
                callback: (data: ResponseData) => {
                    if (productsAbortController.current !== controller) return;
                    productsAbortController.current = null;
                    if (!data.success) {
                        finishLoading();
                        if (!data.handled) showLoadError(data.message);
                        return;
                    }
                    const fetched: QrCodeProduct[] = data.products ?? [];
                    const nextCursor: string | null = data.last_product_ean ?? null;
                    const more = !!data.has_more && nextCursor !== null;
                    cursorRef.current = nextCursor;
                    setHasMore(more);
                    if (params?.append)
                        setProducts(current => {
                            const seen = new Set(current.map((product: QrCodeProduct) => product.id));
                            return current.concat(fetched.filter((product: QrCodeProduct) => !seen.has(product.id)));
                        });
                    else {
                        appliedSearchRef.current = _search;
                        setAppliedSearch(_search ?? '');
                        setProducts(fetched);
                    }
                    if (more && !fetched.length) {
                        loadProducts(load, {
                            ...params,
                            search: _search ?? '',
                            cursor: nextCursor,
                            continued: true,
                            force: true
                        })();
                        return;
                    }
                    finishLoading();
                },
                errorCallback: () => {
                    if (productsAbortController.current !== controller) return;
                    productsAbortController.current = null;
                    finishLoading();
                    showLoadError();
                },
                onAbort: () => {
                    if (productsAbortController.current !== controller) return;
                    productsAbortController.current = null;
                    finishLoading();
                }
            });
            productsAbortController.current = controller;
        };
    }

    function finishLoading() {
        loadingMoreRef.current = false;
        setLoadingProducts(false);
        setLoadingMore(false);
    }

    function cancelRequests() {
        if (productsAbortController.current) {
            productsAbortController.current.abort();
            productsAbortController.current = null;
            finishLoading();
        }
    }

    function clearDebounce() {
        if (searchDebounce.current !== null) {
            window.clearTimeout(searchDebounce.current);
            searchDebounce.current = null;
        }
    }

    useEffect(() => {
        onSetCrumbs([{label: t('qrCodes.crumb', 'QR Codes'), route: QR_CODES_ROUTE}]);
    }, []);

    useEffect(() => {
        cancelRequests();
        setSearch('');
        if (!account) {
            setProducts([]);
            setAppliedSearch('');
            appliedSearchRef.current = null;
            setHasMore(false);
            setLoadingProducts(false);
        } else loadProducts(true, {search: '', force: true})();
        return function () {
            clearDebounce();
            cancelRequests();
        };
    }, [account?.id]);

    return (
        <QrCodes
            onChangeSearch={onChangeSearch}
            onSearch={onSearch}
            onClickDownload={onClickDownload}
            onClickDownloadAll={onClickDownloadAll}
            onRowVisible={onRowVisible}
            products={products}
            search={search}
            appliedSearch={appliedSearch}
            loadingProducts={loadingProducts}
            loadingMore={loadingMore}
            downloadingIds={downloadingIds}
            downloadingAll={downloadingAll}
        />
    );
}
